import React, {useCallback, useMemo, useRef} from "react";
import {useInView} from "motion/react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faBullseye} from "@fortawesome/pro-light-svg-icons/faBullseye";

import {isBetween} from "@/helpers/isBetween.js";
import {emojis} from "@/constants/emojis.js";
import RenderIf from "@/components/Global/RenderIf/RenderIf";
import TargetMark from "./TargetMark/TargetMark";

import {config} from "@/config";
import styles from "./Bar.module.scss";

const initialCustomStyle = {
  rootMargin: "0px 0px -100px 0px",
};

export default function Bar(props) {
  const {
    score,
    scorePercentage,
    target,
    ranges,
    rangeStatus,
    showTarget = true,
    unit = "",
    legendPosition = "bottom",
    customStyle,
    children,
  } = props;
  const barStyle = {...initialCustomStyle, ...customStyle};
  const targetStart = target?.[0]?.y && target?.[0]?.percentage ? Math.min(...target.map(o => o.y)) : 0;
  const targetEnd = target?.[1]?.y && target?.[1]?.percentage ? Math.max(...target.map(o => o.y)) : 0;
  const isTargetHit = isBetween(score, targetStart, targetEnd);
  const hasTarget = targetEnd != 0;
  const isForth = config.websiteId === 1;

  const ref = useRef();
  const inView = useInView(ref, {
    margin: barStyle.rootMargin,
    once: true,
  });

  const rangeActive = useMemo(() => {
    let range = ranges.filter((range, index) => {
      const minValue = ranges[index - 1]?.y || 0;
      const maxValue = range.y;

      return isBetween(parseFloat(score), minValue, maxValue);
    });

    if (range.length > 0) {
      return range[0];
    } else {
      range = ranges.filter((range, index) => {
        let minValue = 0;
        let maxValue = ranges.slice(0, index + 1).reduce((acc, level) => acc + parseFloat(level.percentage), 0);

        if (index > 0) {
          minValue = ranges.slice(0, index).reduce((acc, level) => acc + parseFloat(level.percentage), 0);
          maxValue = ranges.slice(0, index + 1).reduce((acc, level) => acc + parseFloat(level.percentage), 0);
        }

        return isBetween(scorePercentage, minValue, maxValue);
      });

      if (range.length > 0) {
        return range[0];
      }

      return null;
    }
  }, [ranges, score, scorePercentage]);

  const checkRangeActive = useCallback(
    range => {
      if (!range) return false;

      if (rangeStatus?.toLowerCase() == range) {
        return true;
      }

      if (rangeStatus?.toLowerCase() == "borderline high" && range == "border-high") {
        return true;
      }

      if (rangeStatus?.toLowerCase() == "borderline low" && range == "border-low") {
        return true;
      }

      return false;
    },
    [rangeStatus],
  );

  return (
    <div ref={ref}>
      <RenderIf isTrue={legendPosition === "top"}>
        <RenderIf isTrue={isForth && showTarget && hasTarget && isTargetHit}>
          <p className="fw-normal text-success text-center">
            <FontAwesomeIcon icon={faBullseye} /> Target hit <span className={`${styles.emoji}`}>{emojis.success}</span>
          </p>
        </RenderIf>

        <RenderIf isTrue={isForth && showTarget && hasTarget && !isTargetHit}>
          <p className={`${styles.targetCopy} fw-normal text-center`}>
            <FontAwesomeIcon icon={faBullseye} /> Your next target{" "}
            <span className={`${styles.fontBigger}`}>
              {targetStart} - {targetEnd}
            </span>{" "}
            {unit ? `(${unit})` : ""}
          </p>
        </RenderIf>
        {children}
      </RenderIf>

      {inView ? (
        <ul className={styles.bar}>
          {ranges.map(range => (
            <li
              key={range.x}
              data-name={range.x}
              data-active={rangeStatus ? checkRangeActive(range?.x) : rangeActive?.x == range?.x}
              className={styles.slice}
              style={{width: `${range.percentage}%`}}>
              <RenderIf isTrue={isForth && showTarget && hasTarget}>
                <TargetMark ranges={ranges} targetRange={target} score={score} />
              </RenderIf>
            </li>
          ))}
          <span
            className={styles.mark}
            data-name={rangeStatus ? rangeStatus?.toLowerCase() : rangeActive?.x}
            style={{left: `${scorePercentage}%`}}></span>
        </ul>
      ) : (
        <div style={{margin: "40px 0 30px 0", height: "10px"}}></div>
      )}

      <RenderIf isTrue={legendPosition === "bottom"}>
        {children}
        <RenderIf isTrue={isForth && showTarget && hasTarget && isTargetHit}>
          <p className="fw-normal text-success text-center">
            <FontAwesomeIcon icon={faBullseye} /> Target hit <span className={`${styles.emoji}`}>{emojis.success}</span>
          </p>
        </RenderIf>

        <RenderIf isTrue={isForth && showTarget && targetEnd != 0 && !isTargetHit}>
          <p className={`${styles.targetCopy} fw-normal text-center`}>
            <FontAwesomeIcon icon={faBullseye} /> Your next target{" "}
            <span className={`${styles.fontBigger}`}>
              {targetStart} - {targetEnd}
            </span>{" "}
            {unit ? `(${unit})` : ""}
          </p>
        </RenderIf>
      </RenderIf>
    </div>
  );
}
